<template>
  <div class="py-3 ejm-job-row">
    <div class="d-flex align-items-center">
      <EjmJobDetails :job="props.job" class="flex-fill" style="min-width: 0">
        <EjmJobPills :job="props.job" class="mt-1" />
      </EjmJobDetails>
      <div class="d-none d-xxl-block vr mx-3" />
      <div
        v-if="props.showApplicationData && props.job.target_application_count"
        class="d-none d-xl-flex align-items-center gap-3 px-3"
        style="min-width: 200px">
        <div class="flex-fill">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <small class="fw-bold">{{ props.job.application_count }} / {{ props.job.target_application_count }}</small>
            <small class="text-muted">{{ applicationPercent }}%</small>
          </div>
          <div class="progress" style="height: 6px">
            <div
              class="progress-bar"
              :class="progressBarClass"
              :style="{ width: Math.min(applicationPercent, 100) + '%' }" />
          </div>
        </div>
        <span v-if="(props.job.hire_count ?? 0) > 0" class="badge bg-success-lt">
          {{ props.job.hire_count }} {{ props.job.hire_count === 1 ? 'hire' : 'hires' }}
        </span>
      </div>
      <div v-if="props.showApplicationData && props.job.target_application_count" class="d-none d-xl-block vr mx-2" />
      <EjmJobFreshness :job="props.job" class="d-none d-xxl-flex px-3" />
      <div class="d-none d-lg-block vr mx-2" />
      <div class="d-none d-lg-flex align-items-center ps-3">
        <button
          v-if="featureStore.featureCanCreateEditOrder"
          class="btn btn-icon rounded-circle"
          :class="hasOrders ? 'btn-outline-primary' : 'btn-primary'"
          v-tooltip="$t('external_job_markets.create_order')"
          @click="emit('create-order', props.job)">
          <IconPlus :size="20" />
        </button>
      </div>
    </div>
    <div v-if="hasOrders" class="d-flex flex-column gap-2 mt-2 ms-5 ps-3">
      <EjmJobOrderCard v-for="order in props.job.orders" :key="order.id" :order="order" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IconPlus } from '@tabler/icons-vue';
import EjmJobDetails from './EjmJobDetails.vue';
import EjmJobPills from './EjmJobPills.vue';
import EjmJobFreshness from './EjmJobFreshness.vue';
import EjmJobOrderCard from './EjmJobOrderCard.vue';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import type { ExternalJobMarketJob } from '@/types/externalJobMarket';

const props = withDefaults(defineProps<{ job: ExternalJobMarketJob; showApplicationData?: boolean }>(), {
  showApplicationData: false,
});
const emit = defineEmits<{ (e: 'create-order', job: ExternalJobMarketJob): void }>();
const featureStore = useCustomerFeaturesStore();
const hasOrders = computed(() => (props.job.orders?.length ?? 0) > 0);

const applicationPercent = computed(() => {
  if (!props.job.target_application_count) return 0;
  return Math.round(((props.job.application_count ?? 0) / props.job.target_application_count) * 100);
});

const progressBarClass = computed(() => {
  const pct = applicationPercent.value;
  if (pct >= 100) return 'bg-success';
  if (pct >= 50) return 'bg-warning';
  return 'bg-danger';
});
</script>
